import type { ReactNode } from 'react';
import type { HeatmapNavKey } from '../../../services/heatmapNavigationService';
import { isHeatmapNavKey } from '../../../services/heatmapNavigationService';

interface HeatmapNavButtonProps {
  cellId: string;
  isTabStop: boolean;
  className: string;
  label?: string;
  isExpanded?: boolean;
  children: ReactNode;
  onActivate: () => void;
  onNavigate: (cellId: string, key: HeatmapNavKey) => void;
  onCellFocus: (cellId: string) => void;
}

// The row-header cells of the heatmap grid: a skill's expand toggle and a
// person's calendar. They join the day cells' roving tabindex, so the whole grid
// is one tab stop, while activation stays the native button's own — click, Space
// and Enter all work without a handler of ours.
export const HeatmapNavButton = ({
  cellId,
  isTabStop,
  className,
  label,
  isExpanded,
  children,
  onActivate,
  onNavigate,
  onCellFocus
}: HeatmapNavButtonProps) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (!isHeatmapNavKey(e.key)) return;
    // Stops the page scrolling on arrows and jumping on Home/End.
    e.preventDefault();
    onNavigate(cellId, e.key);
  };

  return (
    <button
      type="button"
      className={className}
      tabIndex={isTabStop ? 0 : -1}
      data-heatmap-cell-id={cellId}
      aria-label={label}
      aria-expanded={isExpanded}
      onClick={onActivate}
      onKeyDown={handleKeyDown}
      onFocus={() => onCellFocus(cellId)}
    >
      {children}
    </button>
  );
};
